import { useMemo } from 'react';

// A meridian on a sphere of radius R seen side-on is an ellipse with rx = R·|sin(longitude)|.
// Sampled every 30° over half a turn, so each meridian sweeps across the globe and back.
const R = 10;
const MERIDIAN_RX = [0, 0.5, 0.866, 1, 0.866, 0.5, 0].map((f) => (f * R).toFixed(2)).join(';');
const TURN_SECONDS = 4;
const MERIDIANS = 3;

// Parallels at ±30° latitude are horizontal chords at y = ±R·sin30°, half-width R·cos30°.
const PARALLEL_Y = R * 0.5;
const PARALLEL_HALF = R * 0.866;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

/**
 * Wireframe globe that turns left to right in 3D: the outline and latitude lines stay still
 * while the longitude lines sweep across. Still when the user prefers reduced motion.
 */
const GlobeIcon = ({ className = '' }: { className?: string }) => {
  const animate = useMemo(() => !prefersReducedMotion(), []);

  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      aria-hidden="true"
      className={className}
    >
      <circle cx="12" cy="12" r={R} />
      <line x1={12 - R} y1="12" x2={12 + R} y2="12" />
      <line
        x1={12 - PARALLEL_HALF}
        y1={12 - PARALLEL_Y}
        x2={12 + PARALLEL_HALF}
        y2={12 - PARALLEL_Y}
      />
      <line
        x1={12 - PARALLEL_HALF}
        y1={12 + PARALLEL_Y}
        x2={12 + PARALLEL_HALF}
        y2={12 + PARALLEL_Y}
      />
      {Array.from({ length: MERIDIANS }, (_, index) => (
        <ellipse
          key={index}
          data-meridian
          cx="12"
          cy="12"
          ry={R}
          // Static fallback: meridians spread evenly, as a still globe would show them.
          rx={(R * Math.abs(Math.sin(((index + 0.5) * Math.PI) / MERIDIANS))).toFixed(2)}
        >
          {animate && (
            <animate
              attributeName="rx"
              values={MERIDIAN_RX}
              dur={`${TURN_SECONDS}s`}
              // Negative begins start each meridian part-way through, evenly spaced.
              begin={`${(-(index * TURN_SECONDS) / MERIDIANS).toFixed(2)}s`}
              repeatCount="indefinite"
            />
          )}
        </ellipse>
      ))}
    </svg>
  );
};

export default GlobeIcon;
